import React, { useState, useEffect, useCallback } from "react";
import { BookOpen } from "lucide-react";
import Toast, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, saveStorage, addActivity, getSchoolSettings, loadModuleData, saveModuleData, addActivityAsync } from "./utils";
import StaffSelect from "./StaffSelect";

const EMPTY_DATA = {
  leadership: [], committees: [],
  mainOwner: "", reviewFrequency: "",
  escalationLevel1: "", escalationLevel2: "", ownershipNote: "",
};

const EMPTY_LEADER = { role: "", name: "", area: "", reportsTo: "", note: "" };
const EMPTY_COMMITTEE = { name: "", lead: "", purpose: "", frequency: "", members: "", status: "", note: "" };

function Tag({ color, children }) {
  const colors = {
    green: "bg-[#eaf8f1] text-[#2d9b68] border border-[#b6e8d3]",
    red: "bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0]",
    orange: "bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1]",
    blue: "bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3]",
    gray: "bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3]",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.gray}`}>
      {children}
    </span>
  );
}

function Window({ title, note, headerRight, children }) {
  return (
    <section className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm overflow-hidden">
      <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#e6ecf3]">
        <div>
          <div className="font-semibold text-[#233445] text-[15px]">{title}</div>
          {note && <div className="text-xs text-[#617587] mt-0.5">{note}</div>}
        </div>
        {headerRight && <div className="flex items-center gap-2 shrink-0">{headerRight}</div>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function PreviewCard({ label, value }) {
  return (
    <div className="bg-[#f5f7fb] rounded-[14px] p-4 mb-3 last:mb-0">
      <div className="text-xs font-semibold text-[#617587] uppercase tracking-wide mb-1">{label}</div>
      <div className="text-sm text-[#233445]">{value || "Not added yet."}</div>
    </div>
  );
}

function InfoCard({ title, text }) {
  return (
        <div className="bg-[#eef6fc] rounded-[14px] p-4 mb-3 last:mb-0 border border-[#cfe3f3]">
      <div className="text-sm font-semibold text-[#2d81c1] mb-1">{title}</div>
      <div className="text-sm text-[#617587]">{text}</div>
    </div>
  );
}

const INPUT = "w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] placeholder-[#8ea0b1] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white";
const TEXTAREA = "w-full border border-[#e6ecf3] rounded-[10px] px-4 py-3 text-sm text-[#233445] placeholder-[#8ea0b1] resize-none focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white";

function committeeStatusColor(s) {
  return s === "Active" ? "green" : s === "Needs Clarity" ? "orange" : "red";
}

function calculateCompletion(data) {
  let filled = 0;
  if (data.leadership.length > 0) filled += 1;
  if (data.leadership.length >= 3) filled += 1;
  if (data.committees.length > 0) filled += 1;
  if (data.committees.length >= 3) filled += 1;
  if (data.mainOwner) filled += 1;
  if (data.reviewFrequency) filled += 1;
  if (data.escalationLevel1) filled += 1;
  if (data.ownershipNote) filled += 1;
  return Math.round((filled / 8) * 100);
}

const SAMPLE_DATA = {
  leadership: [
    { role: "Principal", name: "Dr. Meera Sharma", area: "Overall academic and operational leadership", reportsTo: "Director / Management", note: "Leads school vision execution, culture, teacher performance, and student outcomes." },
    { role: "Vice Principal", name: "Arjun Khanna", area: "School operations and discipline", reportsTo: "Principal", note: "Handles day-to-day coordination, discipline systems, and implementation support." },
    { role: "Academic Coordinator", name: "Ritika Saini", area: "Curriculum planning and assessment", reportsTo: "Principal", note: "Supports teacher planning, learning quality, and academic reviews." },
  ],
  committees: [
    { name: "Academic Excellence Committee", lead: "Ritika Saini", purpose: "Improve teaching quality, assessment clarity, and learning outcomes", frequency: "Monthly", members: "6", status: "Active", note: "Reviews lesson quality, assessment practices, and intervention needs." },
    { name: "Student Wellbeing Committee", lead: "Arjun Khanna", purpose: "Monitor student wellbeing, behaviour, and pastoral systems", frequency: "Monthly", members: "5", status: "Active", note: "Tracks discipline trends, wellbeing concerns, and culture issues." },
    { name: "Parent Engagement Committee", lead: "Neha Verma", purpose: "Strengthen parent communication and trust-building", frequency: "Quarterly", members: "4", status: "Needs Clarity", note: "Needs stronger cadence and ownership definition." },
  ],
  mainOwner: "Principal",
  reviewFrequency: "Monthly",
  escalationLevel1: "Vice Principal",
  escalationLevel2: "Director / Management",
  ownershipNote: "All major school improvement reviews begin at committee level, are consolidated by leadership, and escalate to management where major policy or resource decisions are required.",
};

export default function TeamsCommittee() {
  const [activeTab, setActiveTab] = useState("leadership");
  const [data, setData] = useState(EMPTY_DATA);
  const [leader, setLeader] = useState(EMPTY_LEADER);
  const [editingLeaderIdx, setEditingLeaderIdx] = useState(null);
  const [committee, setCommittee] = useState(EMPTY_COMMITTEE);
  const [editingCommitteeIdx, setEditingCommitteeIdx] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [committeeStatusFilter, setCommitteeStatusFilter] = useState("");
  const [committeeFrequencyFilter, setCommitteeFrequencyFilter] = useState("");
  const school = getSchoolSettings();

  useEffect(() => {
    const stored = readStorage(STORAGE_KEYS.teams, null);
    if (stored) {
      setData({ ...EMPTY_DATA, ...stored, leadership: Array.isArray(stored.leadership) ? stored.leadership : [], committees: Array.isArray(stored.committees) ? stored.committees : [] });
    }
    loadModuleData("teams", null).then(d => {
      if (d) setData({ ...EMPTY_DATA, ...d, leadership: Array.isArray(d.leadership) ? d.leadership : [], committees: Array.isArray(d.committees) ? d.committees : [] });
    });
  }, []);

  const saveModule = useCallback((d, action = "Teams and committees saved") => {
    saveModuleData("teams", d, 0);
    addActivityAsync("Teams & Committees", action, "Saved", "green");
  }, []);

  const completion = calculateCompletion(data);
  const ownership = completion >= 85 ? "High" : completion >= 55 ? "Medium" : "Low";

  const ownershipStatus =
    completion >= 85 ? "The school now has a fairly clear operational accountability structure."
    : completion >= 55 ? "There is some ownership clarity, but more structure is still needed."
    : "No ownership structure saved yet.";

  const leadershipInterpretation =
    completion >= 85 ? "Leadership and committee structure look fairly ready for strategic planning and execution."
    : completion >= 55 ? "A basic structure exists, but ownership and committees still need strengthening."
    : "The school still needs a clearer accountability structure.";

  function handleAddLeader() {
    const { role, name, area } = leader;
    if (!role || !name || !area) {
      showToast("Role, Person Name, and Primary Responsibility Area are required.", "error");
      return;
    }
    let updated;
    if (editingLeaderIdx !== null) {
      updated = data.leadership.map((l, i) => i === editingLeaderIdx ? leader : l);
      showToast("Leadership role updated.", "success");
    } else {
      updated = [...data.leadership, leader];
      showToast("Leadership role added.", "success");
    }
    const newData = { ...data, leadership: updated };
    setData(newData);
    saveModule(newData, editingLeaderIdx !== null ? "Leadership role updated" : "Leadership role added");
    setLeader(EMPTY_LEADER);
    setEditingLeaderIdx(null);
  }

  function editLeader(idx) {
    setLeader({ ...data.leadership[idx] });
    setEditingLeaderIdx(idx);
    showToast("Leadership role loaded for editing.", "success");
  }

  function deleteLeader(idx) {
    if (!window.confirm("Delete this leadership role?")) return;
    const updated = data.leadership.filter((_, i) => i !== idx);
    const newData = { ...data, leadership: updated };
    setData(newData);
    saveModule(newData, "Leadership role deleted");
    showToast("Leadership role deleted.", "warn");
    if (editingLeaderIdx === idx) { setLeader(EMPTY_LEADER); setEditingLeaderIdx(null); }
  }

  function handleAddCommittee() {
    const { name, lead, purpose, frequency, status } = committee;
    if (!name || !lead || !purpose || !frequency || !status) {
      showToast("Committee Name, Lead, Purpose, Frequency, and Status are required.", "error");
      return;
    }
    let updated;
    if (editingCommitteeIdx !== null) {
      updated = data.committees.map((c, i) => i === editingCommitteeIdx ? committee : c);
      showToast("Committee updated.", "success");
    } else {
      updated = [...data.committees, committee];
      showToast("Committee added.", "success");
    }
    const newData = { ...data, committees: updated };
    setData(newData);
    saveModule(newData, editingCommitteeIdx !== null ? "Committee updated" : "Committee added");
    setCommittee(EMPTY_COMMITTEE);
    setEditingCommitteeIdx(null);
  }

  function editCommittee(idx) {
    setCommittee({ ...data.committees[idx] });
    setEditingCommitteeIdx(idx);
    showToast("Committee loaded for editing.", "success");
  }

  function deleteCommittee(idx) {
    if (!window.confirm("Delete this committee?")) return;
    const updated = data.committees.filter((_, i) => i !== idx);
    const newData = { ...data, committees: updated };
    setData(newData);
    saveModule(newData, "Committee deleted");
    showToast("Committee deleted.", "warn");
    if (editingCommitteeIdx === idx) { setCommittee(EMPTY_COMMITTEE); setEditingCommitteeIdx(null); }
  }

  function handleSaveOwnership(isDraft = false) {
    if (!data.mainOwner || !data.reviewFrequency) {
      showToast("Main Accountability Owner and Review Frequency are required.", "error");
      return;
    }
    saveModule(data, isDraft ? "Ownership draft saved" : "Ownership structure saved");
    showToast(isDraft ? "Ownership draft saved." : "Ownership structure saved.", "success");
  }

  function handleFillSample() {
    setData(SAMPLE_DATA);
    saveModule(SAMPLE_DATA, "Sample team and committee data loaded");
    showToast("Sample Teams & Committees content loaded.", "success");
  }

  function handleClear() {
    if (!window.confirm("This will clear all saved data in Teams & Committees. Continue?")) return;
    setData(EMPTY_DATA);
    setLeader(EMPTY_LEADER);
    setEditingLeaderIdx(null);
    setCommittee(EMPTY_COMMITTEE);
    setEditingCommitteeIdx(null);
    saveModuleData("teams", EMPTY_DATA, 0);
    addActivityAsync("Teams & Committees", "Module cleared", "Cleared", "red");
    showToast("Teams & Committees module cleared.", "warn");
  }

  const roleOptions = [...new Set(data.leadership.map(l => l.role).filter(Boolean))];
  const COMMITTEE_STATUS_OPTIONS = ["Active", "Needs Clarity", "To Be Formed"];
  const COMMITTEE_FREQUENCY_OPTIONS = ["Weekly", "Monthly", "Quarterly", "Half-Yearly"];

  const filteredLeaders = data.leadership
    .filter(l => !search || `${l.role} ${l.name} ${l.area} ${l.reportsTo}`.toLowerCase().includes(search.toLowerCase()))
    .filter(l => !roleFilter || l.role === roleFilter);

  const filteredCommittees = data.committees
    .filter(c => !search || `${c.name} ${c.lead} ${c.purpose} ${c.status}`.toLowerCase().includes(search.toLowerCase()))
    .filter(c => !committeeStatusFilter || c.status === committeeStatusFilter)
    .filter(c => !committeeFrequencyFilter || c.frequency === committeeFrequencyFilter);

  const tabs = [
    { id: "leadership", label: "Leadership Team" },
    { id: "committees", label: "Committees" },
    { id: "ownership", label: "Ownership & Review" },
    { id: "preview", label: "Live Preview" },
  ];

  const extraControls = (
    <div className="flex items-center gap-2">
      <select
        className="text-sm border border-[#e6ecf3] rounded-[10px] px-3 py-1.5 bg-white text-[#233445] focus:outline-none"
        value={data.reviewFrequency || "Monthly"}
        onChange={e => {
          const newData = { ...data, reviewFrequency: e.target.value };
          setData(newData);
          saveModule(newData, "Review frequency updated");
          showToast("Review frequency updated.", "success");
        }}
      >
        <option value="Monthly">Monthly Review</option>
        <option value="Quarterly">Quarterly Review</option>
        <option value="Half-Yearly">Half-Yearly Review</option>
      </select>
      <button onClick={handleFillSample} className="text-sm px-3 py-1.5 rounded-[10px] bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1] hover:bg-[#ffe8d0] transition-colors" title="Fill sample data">✨</button>
      <button onClick={handleClear} className="text-sm px-3 py-1.5 rounded-[10px] bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0] hover:bg-[#ffe0e0] transition-colors" title="Clear module">⌫</button>
    </div>
  );

  const ownershipColor = ownership === "High" ? "green" : ownership === "Medium" ? "orange" : "red";

  return (
    <>
      <div className="flex-1 flex flex-col min-w-0 border-l-2 border-slate-200 ml-1">

        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
          {/* Hero Header */}
          <section className="mb-8">
            <div className="text-center px-4 py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">Teams & Committees</h1>
              <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 leading-[1.4]">
                Build leadership accountability, committee ownership, and governance review rhythm for connected planning.
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { label: "Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${completion}%`, meta: "leadership and committee coverage" },
              { label: "Leadership Roles", icon: "👥", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: data.leadership.length, meta: "core positions saved" },
              { label: "Committees", icon: "🏫", iconCls: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]", value: data.committees.length, meta: "groups defined" },
              { label: "Ownership Clarity", icon: "⚑", iconCls: ownershipColor === "green" ? "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]" : ownershipColor === "orange" ? "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]" : "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]", value: ownership, meta: "accountability structure" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                <div className="flex items-center justify-between gap-[10px] mb-4">
                  <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                  <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>
                    {s.icon}
                  </div>
                </div>
                <div>
                  <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                  <div className="text-[12px] text-[#617587]">{s.meta}</div>
                </div>
              </div>
            ))}
          </section>

          {/* Progress */}
          <div className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#233445]">Module Completion</span>
              <span className="text-sm font-bold text-[#f47c20]">{completion}%</span>
            </div>
            <div className="h-2 bg-[#f5f7fb] rounded-full overflow-hidden">
              <div className="h-full bg-[#f47c20] rounded-full transition-all duration-500" style={{ width: `${completion}%` }} />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 mb-6 bg-white rounded-[18px] border border-[#e6ecf3] p-1.5 shadow-sm w-fit">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded-[12px] text-sm font-medium transition-colors ${activeTab === t.id ? "bg-[#f47c20] text-white shadow-sm" : "text-[#617587] hover:text-[#233445] hover:bg-[#f5f7fb]"}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* LEADERSHIP TAB */}
          {activeTab === "leadership" && (
            <Window
              title="Leadership Team Structure"
              note="Add key decision-makers and role clarity."
              headerRight={
                <>
                  <button onClick={() => { setLeader(EMPTY_LEADER); setEditingLeaderIdx(null); }} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Reset Input</button>
                  <button onClick={handleAddLeader} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f47c20] text-white hover:bg-[#d96d18] transition-colors">
                    {editingLeaderIdx !== null ? "Update Role" : "Add Leadership Role"}
                  </button>
                </>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Role Title</label>
                  <input className={INPUT} placeholder="Example: Principal" value={leader.role} onChange={e => setLeader(l => ({ ...l, role: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Person Name</label>
                  <input className={INPUT} placeholder="Enter name..." value={leader.name} onChange={e => setLeader(l => ({ ...l, name: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Primary Responsibility Area</label>
                  <input className={INPUT} placeholder="Example: Academic leadership" value={leader.area} onChange={e => setLeader(l => ({ ...l, area: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Reporting To</label>
                  <input className={INPUT} placeholder="Example: Director / Management" value={leader.reportsTo} onChange={e => setLeader(l => ({ ...l, reportsTo: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Role Note</label>
                  <textarea className={TEXTAREA} rows={2} placeholder="Add a short description of what this role handles..." value={leader.note} onChange={e => setLeader(l => ({ ...l, note: e.target.value }))} />
                </div>
              </div>

              {data.leadership.length > 0 && (
                <div className="mb-4 max-w-xs">
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Filter by Role</label>
                  <select className={INPUT} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                    <option value="">All Roles</option>
                    {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              )}
              <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]">
                      {["Role", "Name", "Primary Area", "Reports To", "Actions"].map(h => (
                        <th key={h} className="text-left px-4 py-3 font-semibold text-[#617587] text-xs uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeaders.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-8 text-[#8ea0b1] text-sm">{data.leadership.length ? "No leadership roles match the current filter." : "No leadership roles added yet."}</td></tr>
                    ) : filteredLeaders.map((item, idx) => {
                      const realIdx = data.leadership.indexOf(item);
                      return (
                        <tr key={idx} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe] transition-colors">
                          <td className="px-4 py-3 font-medium text-[#233445]">{item.role}</td>
                          <td className="px-4 py-3 text-[#617587]">{item.name}</td>
                          <td className="px-4 py-3 text-[#617587]">{item.area}</td>
                          <td className="px-4 py-3 text-[#617587]">{item.reportsTo || "—"}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button onClick={() => editLeader(realIdx)} className="px-3 py-1 text-xs rounded-[8px] bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3] hover:bg-[#d4edfa] transition-colors">Edit</button>
                              <button onClick={() => deleteLeader(realIdx)} className="px-3 py-1 text-xs rounded-[8px] bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0] hover:bg-[#ffe0e0] transition-colors">Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Window>
          )}

          {/* COMMITTEES TAB */}
          {activeTab === "committees" && (
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5">
              <Window
                title="Committees & Working Groups"
                note="Add committee ownership, function, and cadence."
                headerRight={
                  <>
                    <button onClick={() => { setCommittee(EMPTY_COMMITTEE); setEditingCommitteeIdx(null); }} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Reset Input</button>
                    <button onClick={handleAddCommittee} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f47c20] text-white hover:bg-[#d96d18] transition-colors">
                      {editingCommitteeIdx !== null ? "Update Committee" : "Add Committee"}
                    </button>
                  </>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Committee Name</label>
                    <input className={INPUT} placeholder="Example: Academic Excellence Committee" value={committee.name} onChange={e => setCommittee(c => ({ ...c, name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Committee Lead</label>
                    <StaffSelect value={committee.lead} onChange={v => setCommittee(c => ({ ...c, lead: v }))} placeholder="Search staff or type a name…" className={INPUT} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Purpose / Focus Area</label>
                    <input className={INPUT} placeholder="Example: Learning quality and assessment review" value={committee.purpose} onChange={e => setCommittee(c => ({ ...c, purpose: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Review Frequency</label>
                    <select className={INPUT} value={committee.frequency} onChange={e => setCommittee(c => ({ ...c, frequency: e.target.value }))}>
                      <option value="">Select frequency</option>
                      <option>Weekly</option><option>Monthly</option><option>Quarterly</option><option>Half-Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Members Count</label>
                    <input type="number" className={INPUT} placeholder="Enter member count..." value={committee.members} onChange={e => setCommittee(c => ({ ...c, members: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Status</label>
                    <select className={INPUT} value={committee.status} onChange={e => setCommittee(c => ({ ...c, status: e.target.value }))}>
                      <option value="">Select status</option>
                      <option>Active</option><option>Needs Clarity</option><option>To Be Formed</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Committee Note</label>
                    <textarea className={TEXTAREA} rows={2} placeholder="Add a short explanation of what this committee actually does..." value={committee.note} onChange={e => setCommittee(c => ({ ...c, note: e.target.value }))} />
                  </div>
                </div>

                {data.committees.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 max-w-lg">
                    <div>
                      <label className="block text-sm font-medium text-[#233445] mb-1.5">Filter by Status</label>
                      <select className={INPUT} value={committeeStatusFilter} onChange={e => setCommitteeStatusFilter(e.target.value)}>
                        <option value="">All Statuses</option>
                        {COMMITTEE_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#233445] mb-1.5">Filter by Frequency</label>
                      <select className={INPUT} value={committeeFrequencyFilter} onChange={e => setCommitteeFrequencyFilter(e.target.value)}>
                        <option value="">All Frequencies</option>
                        {COMMITTEE_FREQUENCY_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]">
                        {["Committee", "Lead", "Purpose", "Frequency", "Status", "Actions"].map(h => (
                          <th key={h} className="text-left px-4 py-3 font-semibold text-[#617587] text-xs uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCommittees.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-[#8ea0b1] text-sm">{data.committees.length ? "No committees match the current filters." : "No committees added yet."}</td></tr>
                      ) : filteredCommittees.map((item, idx) => {
                        const realIdx = data.committees.indexOf(item);
                        return (
                          <tr key={idx} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe] transition-colors">
                            <td className="px-4 py-3 font-medium text-[#233445]">{item.name}</td>
                            <td className="px-4 py-3 text-[#617587]">{item.lead}</td>
                            <td className="px-4 py-3 text-[#617587] max-w-[200px] truncate">{item.purpose}</td>
                            <td className="px-4 py-3 text-[#617587]">{item.frequency}</td>
                            <td className="px-4 py-3"><Tag color={committeeStatusColor(item.status)}>{item.status}</Tag></td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button onClick={() => editCommittee(realIdx)} className="px-3 py-1 text-xs rounded-[8px] bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3] hover:bg-[#d4edfa] transition-colors">Edit</button>
                                <button onClick={() => deleteCommittee(realIdx)} className="px-3 py-1 text-xs rounded-[8px] bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0] hover:bg-[#ffe0e0] transition-colors">Delete</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Window>

              <Window title="Why committees matter" note="How this page helps later modules.">
                <InfoCard title="Action plans need owners" text="When goals are created later, these committees can directly take responsibility for execution and review." />
                <InfoCard title="Reduces generic planning" text="Instead of saying 'the school should do this,' you can later say exactly which team or committee should do it." />
                <InfoCard title="Supports monitoring" text="Review frequency and committee status become useful inputs when monitoring cycles are set up." />
                <InfoCard title="Improves accountability" text="A good structure here makes the rest of the portal feel much more realistic and operational." />
              </Window>
            </div>
          )}

          {/* OWNERSHIP TAB */}
          {activeTab === "ownership" && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <Window
                title="Ownership & Review Logic"
                note="Define how accountability works across the school."
                headerRight={
                  <>
                    <button onClick={() => setData(d => ({ ...d, mainOwner: "", reviewFrequency: "", escalationLevel1: "", escalationLevel2: "", ownershipNote: "" }))} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Reset</button>
                    <button onClick={() => handleSaveOwnership(false)} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f47c20] text-white hover:bg-[#d96d18] transition-colors">Save Ownership Setup</button>
                  </>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Main Accountability Owner</label>
                    <StaffSelect value={data.mainOwner} onChange={v => setData(d => ({ ...d, mainOwner: v }))} placeholder="Search staff or type a name…" className={INPUT} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Review Frequency</label>
                    <select className={INPUT} value={data.reviewFrequency} onChange={e => setData(d => ({ ...d, reviewFrequency: e.target.value }))}>
                      <option value="">Select frequency</option>
                      <option>Weekly</option><option>Monthly</option><option>Quarterly</option><option>Half-Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Escalation Level 1</label>
                    <input className={INPUT} placeholder="Example: Vice Principal" value={data.escalationLevel1} onChange={e => setData(d => ({ ...d, escalationLevel1: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Escalation Level 2</label>
                    <input className={INPUT} placeholder="Example: Director" value={data.escalationLevel2} onChange={e => setData(d => ({ ...d, escalationLevel2: e.target.value }))} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Ownership Note</label>
                    <textarea className={TEXTAREA} rows={3} placeholder="Write how committee reviews, role handovers, and accountability work in the school..." value={data.ownershipNote} onChange={e => setData(d => ({ ...d, ownershipNote: e.target.value }))} />
                  </div>
                </div>
                <div className="mt-4 p-3 bg-[#f5f7fb] rounded-[10px] text-sm text-[#617587]">{ownershipStatus}</div>
              </Window>

              <Window title="Saved Ownership Snapshot" note="Quick view of the current governance logic.">
                <PreviewCard label="Main Accountability Owner" value={data.mainOwner} />
                <PreviewCard label="Review Cadence" value={data.reviewFrequency} />
                <PreviewCard label="Escalation Path" value={[data.escalationLevel1, data.escalationLevel2].filter(Boolean).join(" → ") || null} />
                <PreviewCard label="Ownership Note" value={data.ownershipNote} />
              </Window>
            </div>
          )}

          {/* PREVIEW TAB */}
          {activeTab === "preview" && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <Window title="Live Structure Summary" note="Current people and committee structure at a glance.">
                <PreviewCard
                  label="Leadership Snapshot"
                  value={data.leadership.length
                    ? `${data.leadership.length} leadership role(s) defined, including ${data.leadership.slice(0, 2).map(l => l.role).join(" and ")}.`
                    : "No leadership roles saved yet."}
                />
                <PreviewCard
                  label="Committee Snapshot"
                  value={data.committees.length ? `${data.committees.length} committee/working group(s) defined.` : "No committees saved yet."}
                />
                <PreviewCard
                  label="Governance Snapshot"
                  value={[
                    data.mainOwner ? `Main Owner: ${data.mainOwner}` : "",
                    data.reviewFrequency ? `Review: ${data.reviewFrequency}` : "",
                    data.escalationLevel1 ? `Escalation: ${data.escalationLevel1}` : "",
                  ].filter(Boolean).join(" | ") || "No governance data saved yet."}
                />
                <PreviewCard
                  label="Operational Meaning"
                  value={
                    completion >= 85 ? "The school has a reasonably clear chain of responsibility for future action planning."
                    : completion >= 55 ? "The school has a partly defined accountability structure but still needs stronger role clarity."
                    : "The school accountability structure is not yet clear."
                  }
                />
              </Window>

              <Window title="Readiness Interpretation" note="What this module is telling us right now.">
                <InfoCard title="Leadership clarity" text={leadershipInterpretation} />
                <InfoCard title="Why this matters next" text="Diagnosis, SWOT, and goals become much more actionable when roles and committees are already defined here." />
                <InfoCard title="Next connected page" text="After this, move to Market Dynamics so the school's external context can be understood before deeper diagnosis." />
              </Window>
            </div>
          )}
          </main>
        </div>
      </div>
      <Toast />
    </>
  );
}
